import { useRef } from 'react';

/**
 * Custom hook for handling single-click and double-click events separately
 * @param {function} onSingleClick - Handler for single click
 * @param {function} onDoubleClick - Handler for double click
 * @param {number} delay - Delay in ms to wait for second click (default: 250ms)
 * @returns {object} - Event handler to attach to element
 */
const useDoubleClick = (onSingleClick, onDoubleClick, delay = 250) => {
  const clickTimeout = useRef(null);
  const clickCount = useRef(0);

  const handleClick = (event) => {
    clickCount.current += 1;

    if (clickCount.current === 1) {
      // First click - wait to see if there's a second click
      clickTimeout.current = setTimeout(() => {
        // No second click came, execute single click
        if (onSingleClick) {
          onSingleClick(event);
        }
        clickCount.current = 0;
      }, delay);
    } else if (clickCount.current === 2) {
      // Second click - clear timeout and execute double click
      clearTimeout(clickTimeout.current);
      if (onDoubleClick) {
        onDoubleClick(event);
      }
      clickCount.current = 0;
    }
  };

  return {
    onClick: handleClick
  };
};

export default useDoubleClick;
